import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { getTenantInfo } from '@/utils/tenant';
import { AIDLC_NAME, PRODUCT_BY_ID, PRODUCTS, type ProductId } from '@/config/products';
import { getProductIconComponent } from '@/config/productIcons';
import {
  canOpenProduct,
  getActiveProductId,
  isProductComingSoon,
} from '@/utils/productAccess';
import { launchProduct } from '@/utils/productLaunch';
import { isWorkspaceRouterEnabled } from '@/utils/postLoginNavigation';
import { useGetWorkspaceContextQuery } from '@/store/api/apiSlice';
import { useOrgModuleAccess } from '@/hooks/useOrgModuleAccess';

const PRODUCT_ICONS = Object.fromEntries(
  PRODUCTS.map((product) => [product.id, getProductIconComponent(product.iconKey)])
) as Record<ProductId, ReturnType<typeof getProductIconComponent>>;

const LAUNCHER_WIDTH = 320;
const PORTAL_ID = 'product-switcher-portal';

/** Soft tile backgrounds — matches each product brand color */
const TILE_ICON_BG: Record<ProductId, string> = {
  pm: 'bg-blue-50 dark:bg-blue-950/40',
  code: 'bg-violet-50 dark:bg-violet-950/40',
  ship: 'bg-amber-50 dark:bg-amber-950/40',
  lenc: 'bg-sky-50 dark:bg-sky-950/40',
};

function AppLauncherIcon({ active }: { active?: boolean }) {
  return (
    <svg
      className={`h-[18px] w-[18px] ${active ? 'text-brand' : 'text-text-secondary'}`}
      viewBox="0 0 18 18"
      fill="currentColor"
      aria-hidden
    >
      {[0, 1, 2].map((row) =>
        [0, 1, 2].map((col) => (
          <circle key={`${row}-${col}`} cx={3 + col * 6} cy={3 + row * 6} r="1.35" />
        ))
      )}
    </svg>
  );
}

function computeLauncherStyle(rect: DOMRect): React.CSSProperties {
  const left = Math.max(8, Math.min(rect.left, window.innerWidth - LAUNCHER_WIDTH - 8));
  return {
    position: 'fixed',
    top: rect.bottom + 8,
    left,
    width: LAUNCHER_WIDTH,
    zIndex: 40,
  };
}

export default function ProductSwitcher() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const tenantSettings = (user?.tenant?.settings ?? getTenantInfo()?.settings) as
    | Record<string, unknown>
    | undefined;
  const { productAccess, isEntitled } = useOrgModuleAccess(tenantSettings);
  const { data: workspaceCtx } = useGetWorkspaceContextQuery(undefined, {
    skip: !isWorkspaceRouterEnabled(),
  });
  const launcherIds = workspaceCtx?.launcherProductIds as ProductId[] | undefined;
  const visibleProducts = launcherIds?.length
    ? PRODUCTS.filter((p) => launcherIds.includes(p.id))
    : PRODUCTS;
  const activeProductId = getActiveProductId(location.pathname, location.search);
  const activeProduct = PRODUCT_BY_ID[activeProductId];

  const [open, setOpen] = useState(false);
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({});
  const triggerRef = useRef<HTMLButtonElement>(null);

  const updatePanelPosition = useCallback(() => {
    if (!triggerRef.current) return;
    setPanelStyle(computeLauncherStyle(triggerRef.current.getBoundingClientRect()));
  }, []);

  const closePanel = () => setOpen(false);

  const togglePanel = () => {
    if (open) {
      closePanel();
      return;
    }
    updatePanelPosition();
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    updatePanelPosition();
    const onScrollOrResize = () => updatePanelPosition();
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('scroll', onScrollOrResize, true);
    return () => {
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [open, updatePanelPosition]);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (document.getElementById(PORTAL_ID)?.contains(target)) return;
      closePanel();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closePanel();
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const handleSelect = (productId: ProductId) => {
    closePanel();
    if (canOpenProduct(productId, productAccess, user?.role)) {
      void launchProduct(productId, user?.role, {
        navigate,
        access: productAccess,
      });
      return;
    }
    // For admin previewing non-entitled products, still try launchProduct so it
    // can open in a new tab if a URL is configured (e.g. Code at localhost:5174)
    if (isProductComingSoon(productId, productAccess)) {
      void launchProduct(productId, user?.role, {
        navigate,
        access: productAccess,
        preview: true,
      });
    }
  };

  const panel = open ? (
    <div id={PORTAL_ID} style={panelStyle} role="menu" aria-label={`${AIDLC_NAME} apps`}>
      <div
        className="app-launcher-panel overflow-hidden rounded-xl border border-border bg-surface shadow-xl"
        style={{ boxShadow: '0 12px 40px rgba(15, 23, 42, 0.16)' }}
      >
        <div className="border-b border-border px-4 py-2.5">
          <p className="text-[11px] font-semibold text-text-primary">{AIDLC_NAME} apps</p>
          <p className="text-[10px] text-text-muted mt-0.5">Switch between CoTaskr products</p>
        </div>

        <div className="grid grid-cols-3 gap-1 p-3">
          {visibleProducts.map((product) => {
            const Icon = PRODUCT_ICONS[product.id];
            const entitled = isEntitled(product.id);
            const openable = entitled && canOpenProduct(product.id, productAccess, user?.role);
            const soon = !entitled || isProductComingSoon(product.id, productAccess);
            const isActive = product.id === activeProductId;
            const faded = soon || !openable;

            return (
              <button
                key={product.id}
                type="button"
                role="menuitem"
                onClick={() => handleSelect(product.id)}
                className={`app-launcher-tile group flex flex-col items-center gap-1.5 rounded-lg p-2 transition-all hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 ${
                  isActive ? 'app-launcher-tile--active' : ''
                } ${faded ? 'app-launcher-tile--faded' : ''}`}
                title={
                  openable
                    ? `Open ${product.name}`
                    : `${product.name} — subscribe or coming soon (click to preview)`
                }
              >
                <span
                  className={`app-launcher-tile__icon flex h-11 w-11 items-center justify-center rounded-xl border border-transparent transition-transform group-hover:scale-[1.03] ${TILE_ICON_BG[product.id]} ${
                    isActive ? `ring-2 ring-offset-1 ${product.previewRingClass}` : ''
                  }`}
                >
                  <Icon className={`h-5 w-5 ${product.pillIconColor}`} aria-hidden />
                </span>
                <span className="max-w-full truncate text-center text-[10px] font-semibold leading-tight text-text-primary">
                  {product.shortName}
                </span>
                {soon && (
                  <span className="text-[8px] font-bold uppercase tracking-wide text-text-muted">
                    Soon
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Admin portal shortcut — shown as a prominent row for admins */}
        {(user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN') && (
          <div className="border-t border-border px-3 py-2">
            <button
              type="button"
              onClick={() => {
                closePanel();
                navigate(user?.role === 'SUPER_ADMIN' ? '/admin' : '/org/products');
              }}
              className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-[11.5px] font-semibold text-text-primary transition-colors hover:bg-surface-hover"
            >
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-surface-hover border border-border">
                <svg className="h-3.5 w-3.5 text-text-secondary" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <rect x="2" y="2" width="6" height="6" rx="1" />
                  <rect x="10" y="2" width="6" height="6" rx="1" />
                  <rect x="2" y="10" width="6" height="6" rx="1" />
                  <rect x="10" y="10" width="6" height="6" rx="1" />
                </svg>
              </span>
              <span className="flex flex-col items-start leading-tight">
                <span>{user?.role === 'SUPER_ADMIN' ? 'Platform Admin Portal' : 'Org Admin Portal'}</span>
                <span className="text-[10px] font-normal text-text-muted">
                  {user?.role === 'SUPER_ADMIN' ? 'Manage tenants, billing & platform' : 'Members, products & settings'}
                </span>
              </span>
              <svg className="ml-auto h-3.5 w-3.5 text-text-muted" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M6 4l4 4-4 4" />
              </svg>
            </button>
          </div>
        )}
        <div className="flex items-center justify-between border-t border-border px-3 py-2 bg-surface-hover/40">
          <button
            type="button"
            onClick={() => {
              closePanel();
              navigate('/workspace');
            }}
            className="rounded-md px-2 py-1 text-[11px] font-semibold text-brand transition-colors hover:bg-brand-light"
          >
            All apps
          </button>
          <span className="text-[9px] text-text-muted">
            Powered by <span className="font-semibold text-text-secondary">AiLenc · CoTaskr</span>
          </span>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={togglePanel}
        className={`app-launcher-trigger flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-transparent transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30 ${
          open ? 'bg-surface-hover border-border' : ''
        }`}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={`Open ${AIDLC_NAME} app launcher — current: ${activeProduct.name}`}
        title={`${AIDLC_NAME} apps · ${activeProduct.shortName}`}
        data-tour="app-launcher"
      >
        <AppLauncherIcon active={open} />
      </button>
      {panel && createPortal(panel, document.body)}
    </>
  );
}
